import { createHelpers, getField, updateField } from 'vuex-map-fields'

import filter from '@/services/http/api/filter'
import resource from '@/services/http/api/resource'
import storage from '@/services/storage/sessionStorage'

import { camelToSnakeCase, snakeToCamelCase } from '@/helpers/caseStyles'
import paramsDictionary from '@/helpers/paramsDictionary'

const { getFiltersVisibilityField, updateFiltersVisibilityField } = createHelpers({
  getterType: 'getFiltersVisibilityField',
  mutationType: 'updateFiltersVisibilityField'
})

const PARAMETERS_KEY = 'params'
const HIDDEN_INPUTS_KEY = 'hilist'

const childrenList = (number = 0, highlighted = [], regular = []) => ({
  number,
  highlighted,
  regular
})

const defaultParameters = () => ({
  openFundraising: false,
  futureProfessions: [],
  personalDifficulties: [],
  provinces: [],
  genders: null,
  firstNames: []
})

const defaultPagination = () => ({
  limit: 6,
  offset: 0
})

export default {
  namespaced: true,
  state: {
    loader: null,
    childrenList: childrenList(),
    resourceFilter: null,
    pagination: defaultPagination(),
    parameters: defaultParameters(),
    appliedURLParams: null,
    hiddenInputsList: storage.get(HIDDEN_INPUTS_KEY, true) || [],
    filtersVisibility: false
  },
  getters: {
    getLoader: (state) => state.loader,
    getChildrenList: (state) => state.childrenList,
    getResourceFilter: (state) => state.resourceFilter,
    getPagination: (state) => state.pagination,
    getParameters: (state) => state.parameters,
    getParametersField(state) {
      return getField(state.parameters)
    },
    getAppliedFilters: (state) => state.appliedURLParams,
    getHiddenInputsList: (state) => state.hiddenInputsList,
    getFiltersVisibility: (state) => state.filtersVisibility,
    getFiltersVisibilityField
  },
  mutations: {
    setLoader(state, payload) {
      state.loader = payload
    },
    setChildrenList(state, payload) {
      const { data } = payload
      state.childrenList = childrenList(data.number, data.highlighted, data.regular)
    },
    addChildrenToList(state, payload) {
      const { data } = payload
      if (data.highlighted) {
        state.childrenList.highlighted.push(...data.highlighted)
      }
      state.childrenList.regular.push(...data.regular)
    },
    clearChildrenList(state) {
      state.childrenList = childrenList()
    },
    setResourceFilter(state, payload) {
      state.resourceFilter = payload
    },
    setPagination(state, field) {
      state.pagination[field.path] = field.value
    },
    clearPagination(state) {
      state.pagination = defaultPagination()
    },
    updateParametersField(state, field) {
      updateField(state.parameters, field)
    },
    setParameters(state, payload) {
      const defaultParams = defaultParameters()
      state.parameters = {
        ...defaultParams,
        ...payload
      }
    },
    setAppliedURLParams(state, payload) {
      state.appliedURLParams = payload
    },
    toggleInputVisibility(state, key) {
      if (state.hiddenInputsList.find((el) => el === key)) {
        state.hiddenInputsList = state.hiddenInputsList.filter((el) => el !== key)
      } else {
        state.hiddenInputsList.push(key)
      }
      storage.set(HIDDEN_INPUTS_KEY, state.hiddenInputsList, true)
    },
    clearParametersStorage() {
      storage.delete(PARAMETERS_KEY)
    },
    setFiltersVisibility(state, payload) {
      state.filtersVisibility = payload
    },
    updateFiltersVisibilityField
  },
  actions: {
    getFilter({ commit, getters }) {
      const pagination = getters.getPagination
      const parameters = getters.getParameters

      const urlSearchParams = new URLSearchParams()
      Object.keys(parameters).forEach((key) => {
        const paramValue = parameters[key]
        const paramKey = paramsDictionary[key] ? paramsDictionary[key] : key
        if (paramValue == null) return
        if (paramValue === true) {
          urlSearchParams.append(camelToSnakeCase(key), parameters[key])
        }
        if (paramValue.length > 0) {
          Object.entries(paramValue).forEach((value) => {
            if (value[1]) {
              urlSearchParams.append(`${camelToSnakeCase(paramKey)}[]`, value[1])
            }
          })
        }
        if (typeof paramValue === 'number') {
          urlSearchParams.append(`${camelToSnakeCase(paramKey)}[]`, paramValue)
        }
      })
      urlSearchParams.append('offset', pagination.offset)

      commit('setLoader', true)
      return filter
        .getFilter(urlSearchParams)
        .then(({ data }) => {
          if (pagination.offset > 0) {
            commit('addChildrenToList', data)
          } else {
            commit('setChildrenList', data)
          }
          return data.data
        })
        .finally(() => {
          commit('setAppliedURLParams', urlSearchParams)
          storage.set(PARAMETERS_KEY, parameters, true)
          if (urlSearchParams.toString() === '') {
            commit('clearParametersStorage')
          }
          commit('setLoader', false)
        })
    },
    getResourceFilter({ commit }) {
      resource.getFilter().then(({ data }) => {
        commit('setResourceFilter', data.data)
        return data.data
      })
    },
    removeFilter({ dispatch, commit, getters }, payload) {
      const parameters = getters.getParameters
      const { key } = payload
      let paramKey = Object.entries(paramsDictionary).find((el) => camelToSnakeCase(el[1]) === key)
      paramKey = paramKey ? paramKey[0] : key
      paramKey = snakeToCamelCase(paramKey)

      let value = null
      if (parameters[paramKey].length) {
        value = parameters[paramKey].filter((el) => el !== payload.value && el !== payload.id)
      }
      commit('updateParametersField', {
        path: paramKey,
        value
      })
      dispatch('getFilter')
    },
    toggleInputVisibility({ commit }, payload) {
      commit('toggleInputVisibility', payload)
    }
  }
}
